import * as React from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * O indicador global de "Carregando".
 *
 * POR QUE GLOBAL, e não um por painel
 * -----------------------------------
 * A primeira versão pôs um overlay em cada tela. Cobria a recarga de fundo e
 * não cobria o que mais incomoda: trocar de página. Nesse intervalo o painel
 * antigo já saiu, o novo ainda não montou, e a tela fica — nas palavras de
 * quem reportou — "intacta", como se o clique não tivesse acontecido. A
 * reação natural é clicar de novo.
 *
 * `useIsFetching` conta TODA consulta em voo, de qualquer tela, inclusive as
 * que ainda estão no primeiro carregamento. `useIsMutating` acrescenta as
 * gravações, que são as esperas que mais importam: faturar, aprovar, alocar.
 * Um só lugar passa a responder por toda a aplicação — separação, romaneios,
 * etiquetas, faturamento, recebimento, sem precisar lembrar de cada tela.
 *
 * POR QUE COBRIR A TELA INTEIRA
 * -----------------------------
 * A versão anterior era um chip no canto inferior direito. Discreto demais:
 * quem reportou só descobriu que ele existia depois de eu apontar, tendo
 * passado por várias esperas sem ver nada. Um aviso que ninguém nota não é
 * discreto, é ausente — e o custo dele é o clique repetido numa tela que
 * grava.
 *
 * Agora é um véu sobre a página toda. Continua leve: a cor de fundo mal
 * cobre, o conteúdo permanece legível por baixo, e o cartão fica no centro,
 * onde o olho já está. O que mudou não foi o volume do aviso, foi o lugar.
 *
 * QUEM BLOQUEIA CLIQUE, E QUEM NÃO
 * --------------------------------
 * Esta é a única diferença de comportamento, e ela é deliberada:
 *
 *  - **Gravação** (`useIsMutating`) bloqueia. Faturar, aprovar, alocar,
 *    transferir — clicar duas vezes aqui é criar dois lançamentos, e o véu é
 *    a trava mais barata contra isso.
 *  - **Consulta** não bloqueia (`pointer-events-none`). Recarregar uma lista
 *    não pode impedir a pessoa de navegar; travar a tela a cada refetch de
 *    fundo transformaria o aviso em estorvo.
 *
 * DISCRETO CONTINUA SENDO REQUISITO
 * ---------------------------------
 * Só aparece depois de 400 ms. Consulta rápida não pode piscar: um véu que
 * cobre e some em 150 ms cansa mais do que a espera que ele anuncia.
 */
export function IndicadorCarregando(): JSX.Element | null {
  const consultas = useIsFetching();
  const gravacoes = useIsMutating();
  const ocupado = consultas + gravacoes > 0;
  const gravando = gravacoes > 0;

  const [visivel, definirVisivel] = React.useState(false);

  React.useEffect(() => {
    if (!ocupado) {
      definirVisivel(false);
      return;
    }
    const t = window.setTimeout(() => definirVisivel(true), 400);
    return () => window.clearTimeout(t);
  }, [ocupado]);

  if (!visivel) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[60] grid place-items-center bg-fundo/45 backdrop-blur-[1.5px]',
        // Só a gravação segura o clique. Ver o comentário acima.
        gravando ? 'cursor-progress' : 'pointer-events-none',
      )}
      /* `polite`: avisa quem usa leitor de tela sem interromper o que ele lê. */
      role="status"
      aria-live="polite"
    >
      {/* Barra fina no topo — o mesmo sinal para quem já estava olhando a
          borda da tela, de graça. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[2px] overflow-hidden bg-primaria/15"
      >
        <span className="block h-full w-1/3 animate-[deslizar_1.1s_ease-in-out_infinite] bg-primaria" />
      </span>

      <span className="flex items-center gap-2.5 rounded-full border border-borda bg-superficie px-4 py-2 text-[13px] font-semibold shadow-forte">
        <Loader2 className="h-4 w-4 animate-spin text-primaria" />
        {gravando ? 'Gravando' : 'Carregando'}
        {gravando ? (
          <span className="text-textoFraco">· não feche a tela</span>
        ) : null}
      </span>
    </div>
  );
}
